import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { REFERRER_TYPE, PAGE_TYPE } from '../../constants/gaEnums';
import { trackMenuClick } from '../../lib/gaEvents';
import { getPageTypeFromPath } from '../../lib/gaParams';
import { MdManageAccounts } from 'react-icons/md';
import { Search } from '@mui/icons-material';
import { Box, useMediaQuery } from '@mui/material';
import logo from '../../assets/logo_black.png';
import logoWhite from '../../assets/logo_white.png';
import { useUserStore } from '../../stores/userStore';
import HeaderMobile from './HeaderMobile';
import HeaderSearchBar from '../common/HeaderSearchBar';
import TabletSearchBar from '../common/TabletSearchBar';
import HeaderProfileButton from '../common/HeaderProfileButton';
import ColorSchemeButton from '../common/ColorSchemeButton';
import theme from '../../theme';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Header = () => {
  const isAdmin = useUserStore((state) => state.isAdmin);
  const user = useUserStore((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { resolvedMode } = useColorScheme();
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();
  const currentPageType = getPageTypeFromPath(location.pathname);

  if (isMobile) {
    return <HeaderMobile />;
  }

  const navLinkStyle = {
    color: theme.palette.black[6],
    textDecoration: 'none',
    fontWeight: 400,
    fontSize: 16,
    whiteSpace: 'nowrap',
    '&:hover': {
      color: theme.palette.black[4],
    },
  };

  const iconBtnStyle = {
    color: theme.palette.black[3],
    background: 'none',
    border: 'none',
    padding: { md: '4px', lg: '10px' },
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: 24,
  };

  const handleNavClick = () => {
    if (showSearch) {
      setShowSearch(false);
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        backgroundColor: theme.palette.surface.main,
        zIndex: 1201,
        borderBottom: `1px solid ${theme.palette.grey.line}`,
        transition: 'background-color 160ms ease, border-color 160ms ease',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          height: 60,
          display: { md: 'grid', lg: 'flex' },
          gridTemplateColumns: { md: '1fr auto 1fr', lg: 'none' },
          alignItems: 'center',
          justifyContent: { lg: 'flex-start' },
          gap: { lg: 6.25 },
          mx: 'auto',
          px: { md: 2.5, lg: 0 },
        }}
      >
        {/* 로고 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            flexShrink: 0,
            justifySelf: { md: 'start', lg: 'auto' },
            pl: { md: 0, lg: '6px' },
          }}
        >
          <Link
            to="/"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
          >
            <Box
              component="img"
              src={resolvedMode === 'dark' ? logoWhite : logo}
              alt="logo"
              sx={{ width: { xs: 91, md: 104, lg: 91 }, height: { xs: 24, md: 27, lg: 24 } }}
            />
          </Link>
        </Box>

        {/* 메뉴 */}
        <Box
          component="nav"
          sx={{
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            flexWrap: 'nowrap',
            flexShrink: 0,
            height: 60,
            justifySelf: { md: 'center', lg: 'auto' },
          }}
        >
          <Box
            component={Link}
            to="/albums"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            앨범
          </Box>
          <Box
            component={Link}
            to="/reviews"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            리뷰
          </Box>
          <Box
            component={Link}
            to="/lists"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.LISTS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            플리
          </Box>
          <Box
            component={Link}
            to="/feed"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={handleNavClick}
            sx={navLinkStyle}
          >
            피드
          </Box>
          <Box
            component={Link}
            to="/notice"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.NOTICE_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            공지
          </Box>
          <Box
            component={Link}
            to="/contact"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick({ targetPageType: 'contact', pageType: currentPageType }, user);
            }}
            sx={navLinkStyle}
          >
            문의
          </Box>
        </Box>

        {/* 검색창 (lg) */}
        {!isTablet && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HeaderSearchBar pageType={currentPageType} />
          </Box>
        )}

        {/* 아이콘 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: { md: 1, lg: 0 },
            justifySelf: { md: 'end', lg: 'auto' },
          }}
        >
          {/* 검색 아이콘 (md) */}
          {isTablet && (
            <Box
              component="button"
              onClick={() => setShowSearch(!showSearch)}
              sx={iconBtnStyle}
              title="검색"
            >
              <Search sx={{ fontSize: 24 }} />
            </Box>
          )}
          <ColorSchemeButton
            className="header-color-scheme-toggle"
            iconSize={24}
            sx={iconBtnStyle}
          />
          {isAdmin && (
            <Box
              component={Link}
              to="/admin"
              onClick={handleNavClick}
              sx={iconBtnStyle}
              title="관리자 페이지"
            >
              <MdManageAccounts size={24} />
            </Box>
          )}
          <HeaderProfileButton variant={isTablet ? 'tablet' : 'desktop'} onClick={handleNavClick} />
        </Box>
      </Box>

      {/* 검색창 드롭다운 (md) */}
      {isTablet && (
        <TabletSearchBar
          isOpen={showSearch}
          onClose={() => setShowSearch(false)}
          pageType={currentPageType}
        />
      )}
    </Box>
  );
};

export default Header;
